import { API } from '@/config/api';
import { OrderItem } from '@/models/order';
import { PositionItem } from '@/models/position';
import { ProfitItem } from '@/models/entrust';
import { ScrollPageInfo } from '@/models/data';
import { BigNumber } from 'bignumber.js';
import { welfareGet, welfarePost } from './api';
import { toFuturesSymbolIdentity } from './utils';
import {
  ExperienceCursorPage,
  ExperienceEntrustDisplay,
  ExperienceOrderDisplay,
  ExperiencePositionDisplay,
  ExperienceRecordSource,
  MarginSourceFilter,
} from './types';

export const MARGIN_SOURCE_OPTIONS: MarginSourceFilter[] = ['ALL', 'NORMAL', 'POSITION_EXPERIENCE'];

export const EXPERIENCE_SOURCE: ExperienceRecordSource = 'POSITION_EXPERIENCE';
export const NORMAL_SOURCE: ExperienceRecordSource = 'NORMAL';

const asList = <T>(data: T[] | ExperienceCursorPage<T> | null | undefined): T[] => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

const cleanParams = <T extends Record<string, unknown>>(params?: T) => {
  if (!params) return undefined;
  const next: Record<string, unknown> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      next[key] = value;
    }
  });
  return Object.keys(next).length ? next : undefined;
};

/** 保证金筛选 → 原合约聚合 source；NORMAL 不传以保持旧分页 */
export const toContractSourceParam = (
  filter: MarginSourceFilter,
): ExperienceRecordSource | 'ALL' | undefined => {
  if (filter === 'ALL') return 'ALL';
  if (filter === 'POSITION_EXPERIENCE') return EXPERIENCE_SOURCE;
  return undefined;
};

export const isExperienceRecord = (item?: {
  source?: string;
  isExperiencePosition?: boolean;
  experiencePosition?: boolean;
  balanceType?: string;
  positionSource?: string;
} | null) => {
  if (!item) return false;
  if (item.source === EXPERIENCE_SOURCE || item.isExperiencePosition || item.experiencePosition) return true;
  if (item.positionSource === 'EXPERIENCE' || item.balanceType === 'EXPERIENCE') return true;
  return false;
};

export const recordSourceKey = (item: object, id: string | number) =>
  `${(item as any).source || (isExperienceRecord(item as any) ? EXPERIENCE_SOURCE : NORMAL_SOURCE)}:${id}`;

const firstPositivePrice = (...values: Array<string | number | null | undefined>) => {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const price = new BigNumber(value);
    if (price.isFinite() && price.gt(0)) return price.toFixed();
  }
  return undefined;
};

const pickEntrustTriggerPrice = (item: any, type: 'TAKE_PROFIT' | 'STOP_LOSS') => {
  const list = item?.entrusts || item?.profitList;
  if (!Array.isArray(list)) return undefined;
  const matched = list.find((row: any) => {
    const entrustType = String(row?.entrustType || row?.type || '').toUpperCase();
    if (type === 'TAKE_PROFIT') return entrustType === 'TAKE_PROFIT' || entrustType === 'PROFIT';
    return entrustType === 'STOP_LOSS' || entrustType === 'STOP';
  });
  if (!matched) return undefined;
  return type === 'TAKE_PROFIT'
    ? matched.triggerPrice ?? matched.triggerProfitPrice ?? matched.takeProfitPrice
    : matched.triggerPrice ?? matched.triggerStopPrice ?? matched.stopLossPrice;
};

/** 体验仓与普通仓止盈止损字段不同，统一解析成列表使用的 trigger*Price */
export const resolvePositionTpslPrices = (item?: Record<string, any> | null) => {
  if (!item) return { triggerProfitPrice: undefined, triggerStopPrice: undefined };
  const list = item?.entrusts || item?.profitList;
  const fromEntrustProfit = pickEntrustTriggerPrice(item, 'TAKE_PROFIT');
  const fromEntrustStop = pickEntrustTriggerPrice(item, 'STOP_LOSS');

  // 有委托集合时以集合为准：空数组表示已撤，忽略仓位上残留的 takeProfitPrice/stopLossPrice
  if (Array.isArray(list)) {
    return {
      triggerProfitPrice: firstPositivePrice(fromEntrustProfit),
      triggerStopPrice: firstPositivePrice(fromEntrustStop),
    };
  }

  return {
    triggerProfitPrice: firstPositivePrice(
      item.takeProfitPrice,
      item.triggerProfitPrice,
      fromEntrustProfit,
    ),
    triggerStopPrice: firstPositivePrice(
      item.stopLossPrice,
      item.triggerStopPrice,
      fromEntrustStop,
    ),
  };
};

const mapEntrustStatus = (status?: string) => {
  const value = String(status || '').toUpperCase();
  if (value === 'ACTIVE' || value === 'TRIGGERING') return 'UNFINISHED';
  if (value === 'TRIGGERED') return 'HISTORY';
  if (value === 'CANCELED' || value === 'EXPIRED') return 'HISTORY';
  return value || 'UNFINISHED';
};

/** 体验仓 / 聚合仓位项 → PositionItem（兼容独立接口与 getPositionList?source=） */
export const mapExperiencePositionToPositionItem = (
  item: ExperiencePositionDisplay | Record<string, any>,
): PositionItem & {
  source: ExperienceRecordSource;
  isExperiencePosition: boolean;
  canOperate?: boolean;
  priceStale?: boolean | null;
  experienceStatus?: string;
  principalAmount?: string;
} => {
  const quantity = String(item.positionQuantity ?? item.positionSize ?? '0');
  const available = String(item.availableCloseQuantity ?? item.availableCloseSize ?? quantity);
  const status = String(item.status || '').toUpperCase();
  const canOperate = item.canOperate !== false && status !== 'CLOSING' && status !== 'CLOSED';
  const symbol = toFuturesSymbolIdentity(item.symbol);
  const principalAmount = String(item.principalAmount ?? item.isolatedMargin ?? '0');
  const snapshotPnl = String(item.unrealizedPnl ?? item.floatProfit ?? '0');
  const floatProfitRate =
    item.floatProfitRate ??
    (new BigNumber(principalAmount).gt(0)
      ? new BigNumber(snapshotPnl).div(principalAmount).times(100).valueOf()
      : '0');
  return {
    source: EXPERIENCE_SOURCE,
    isExperiencePosition: true,
    experiencePosition: true,
    experienceStatus: status,
    canOperate,
    priceStale: item.priceStale,
    positionId: String(item.positionId),
    symbol,
    symbolName: symbol,
    positionSide: item.positionSide as PositionSide,
    positionType: (item.positionType || 'ISOLATED') as PositionType,
    positionModel: (item.positionModel || 'AGGREGATION') as PositionMode,
    leverage: Number(item.leverage || 0),
    entryPrice: String(item.entryPrice ?? '0'),
    positionSize: quantity,
    availableCloseSize: canOperate ? available : '0',
    isolatedMargin: principalAmount,
    initialMargin: principalAmount,
    principalAmount,
    liquidationPrice: String(item.liquidationPrice ?? ''),
    floatProfit: snapshotPnl,
    floatProfitRate,
    realizedProfit: String(item.realizedPnl ?? item.realizedProfit ?? '0'),
    unsettledProfit: '0',
    autoMargin: false,
    closeOrderSize: 0,
    openOrderMarginFrozen: '0',
    state: status === 'CLOSING' ? 2 : 1,
    contractType: undefined as any,
    maintMargin: '0',
    total: 0,
    contractSize: 1,
    welfareAccount: true,
    userCouponId: item.userCouponId,
    expireTime: item.expireTime,
    markPrice: item.markPrice != null ? String(item.markPrice) : undefined,
    // 体验仓用 takeProfitPrice/stopLossPrice/entrusts，普通仓用 triggerProfitPrice/triggerStopPrice
    profitId: item.takeProfitEntrustId ?? item.stopLossEntrustId ?? item.profitId ?? undefined,
    entrusts: (item as any).entrusts,
    profitList: (item as any).profitList,
    takeProfitEntrustId: (item as any).takeProfitEntrustId,
    stopLossEntrustId: (item as any).stopLossEntrustId,
    takeProfitPrice: (item as any).takeProfitPrice,
    stopLossPrice: (item as any).stopLossPrice,
    ...resolvePositionTpslPrices(item),
    triggerProfitConditionType: item.takeProfitConditionType ?? item.triggerProfitConditionType ?? undefined,
    triggerProfitConditionValue:
      item.takeProfitConditionValue != null
        ? String(item.takeProfitConditionValue)
        : item.triggerProfitConditionValue != null
          ? String(item.triggerProfitConditionValue)
          : undefined,
    triggerStopConditionType: item.stopLossConditionType ?? item.triggerStopConditionType ?? undefined,
    triggerStopConditionValue:
      item.stopLossConditionValue != null
        ? String(item.stopLossConditionValue)
        : item.triggerStopConditionValue != null
          ? String(item.triggerStopConditionValue)
          : undefined,
  } as any;
};

export type ExperienceLivePnlContext = {
  getFloatProfit: (position: PositionItem) => string;
  getSymbolConfig: (symbol: string) => { contractSize?: number; pricePrecision?: number; quoteCoinDisplayPrecision?: number } | undefined;
  markPriceMap: Record<string, string | number | undefined>;
  symbolCurrentPriceMap: Record<string, string | number | undefined>;
};

/**
 * 体验仓未实现盈亏随标记价实时刷新（与普通持仓 positionListWithConfig 一致）
 * 收益率 = 未实现盈亏 / 仓位保证金(principalAmount)
 */
export const enrichExperiencePositionWithLivePnl = (
  item: PositionItem,
  ctx: ExperienceLivePnlContext,
): PositionItem => {
  const { getFloatProfit, getSymbolConfig, markPriceMap, symbolCurrentPriceMap } = ctx;
  const symbol = toFuturesSymbolIdentity(item.symbol);
  const symbolConfig = getSymbolConfig(symbol);
  const markPrice = symbolCurrentPriceMap[symbol] || markPriceMap[symbol];
  const margin = String(
    item.isolatedMargin || item.initialMargin || (item as any).principalAmount || '0',
  );
  const normalized: PositionItem = {
    ...item,
    symbol,
    symbolName: symbol,
    ...(symbolConfig
      ? {
          contractSize: symbolConfig.contractSize,
          pricePrecision: symbolConfig.pricePrecision,
          quoteCoinDisplayPrecision: symbolConfig.quoteCoinDisplayPrecision,
        }
      : {}),
  };
  const hasLivePrice = Boolean(markPrice && symbolConfig?.contractSize);
  const snapshotPnl = String(item.floatProfit ?? (item as any).unrealizedPnl ?? '0');
  const floatProfit = hasLivePrice ? getFloatProfit(normalized) : snapshotPnl;
  const floatProfitRate = new BigNumber(margin).gt(0)
    ? new BigNumber(floatProfit).div(margin).times(100).valueOf()
    : '0';
  return { ...normalized, floatProfit, floatProfitRate };
};

/** 仅体验仓走字段映射。普通仓也可能带 principalAmount，不能据此丢掉 triggerProfitPrice/triggerStopPrice */
const shouldMapExperiencePosition = (item: any) => {
  if (isExperienceRecord(item)) return true;
  if (item?.source === NORMAL_SOURCE || item?.positionSize != null) return false;
  return item?.positionQuantity != null || item?.principalAmount != null;
};

/** 聚合列表项规范化：体验仓字段映射，普通仓补 source=NORMAL */
export const normalizeContractPositionList = (list: any[]): PositionItem[] => {
  if (!Array.isArray(list)) return [];
  return list.map(item => {
    if (shouldMapExperiencePosition(item)) {
      return mapExperiencePositionToPositionItem(item);
    }
    return {
      ...item,
      source: item?.source || NORMAL_SOURCE,
    };
  });
};

/** 体验仓止盈止损 → 合约页 ProfitItem 形状 */
export const mapExperienceEntrustToProfit = (item: ExperienceEntrustDisplay): ProfitItem & {
  source: ExperienceRecordSource;
  isExperiencePosition?: boolean;
} => {
  const isTp = item.entrustType === 'TAKE_PROFIT';
  const trigger = String(item.triggerPrice ?? '');
  return {
    source: EXPERIENCE_SOURCE,
    isExperiencePosition: true,
    profitId: String(item.entrustId),
    positionId: String(item.positionId),
    symbol: item.symbol,
    positionSide: item.positionSide || 'LONG',
    positionType: 'ISOLATED',
    positionModel: 'AGGREGATION',
    leverage: item.leverage || 0,
    triggerPrice: trigger,
    triggerPriceSide: isTp ? 1 : 2,
    triggerProfitPrice: isTp ? trigger : '',
    triggerStopPrice: isTp ? '' : trigger,
    triggerPriceType: 'MARK_PRICE',
    origQty: String(item.quantity ?? ''),
    executedQty: item.status === 'TRIGGERED' ? String(item.quantity ?? '') : undefined,
    entryPrice: '',
    currentPrice: '',
    activatePrice: '',
    state: mapEntrustStatus(item.status),
    createdTime: item.createdTime,
    updatedTime: item.updatedTime,
    triggerTime: item.triggerTime || undefined,
    expireTime: item.expireTime,
    canOperate: item.canOperate,
    isUBased: true,
    unrealizedPnl: '',
    distanceRate: '',
    contractSize: '1',
    liquidationPrice: '',
    pricePrecision: 2,
    /** 体验仓委托在 UI 上按 NORMAL 展示；全仓 Tab 仍合并 TP/SL 两条委托 */
    profitType: 'ALL',
  } as any;
};

/** 体验仓虚拟成交 → 合约页 OrderItem 形状（历史订单） */
export const mapExperienceOrderToHistory = (item: ExperienceOrderDisplay): OrderItem & {
  source: ExperienceRecordSource;
  isExperiencePosition?: boolean;
  positionType?: PositionType;
  positionModel?: PositionMode;
  leverage?: number;
} => {
  const price = String(item.executionPrice ?? '');
  const qty = String(item.quantity ?? '');
  const isOpen = item.operationType === 'OPEN';
  return {
    source: EXPERIENCE_SOURCE,
    isExperiencePosition: true,
    orderId: String(item.orderId),
    symbol: item.symbol,
    positionSide: item.positionSide,
    orderSide: isOpen
      ? item.positionSide === 'LONG'
        ? 'BUY'
        : 'SELL'
      : item.positionSide === 'LONG'
        ? 'SELL'
        : 'BUY',
    orderType: 'MARKET',
    origQty: qty,
    executedQty: qty,
    price,
    avgPrice: price,
    state: item.status === 'SUCCESS' ? ('FILLED' as any) : ('REJECTED' as any),
    createdTime: item.finalTime || item.createdTime,
    sourceId: String(item.positionId || ''),
    closePosition: !isOpen,
    marginFrozen: '0',
    timeInForce: 'IOC',
    closeProfit: '',
    contractSize: '1',
    fee: 0,
    positionType: 'ISOLATED',
    positionModel: 'AGGREGATION',
    leverage: item.leverage || 0,
  } as any;
};

export const tagNormalSource = <T extends object>(list: T[]): T[] =>
  list.map(item => ({
    ...(item as object),
    source: (item as any).source || NORMAL_SOURCE,
  })) as T[];

export const mergeBySourceId = <T extends object>(
  normal: T[],
  experience: T[],
  getId: (item: T) => string,
): T[] => {
  const map = new Map<string, T>();
  // 聚合接口（含实时盈亏）优先；独立体验仓接口仅作补位
  [...normal, ...experience].forEach(item => {
    const key = recordSourceKey(item, getId(item));
    if (!map.has(key)) map.set(key, item);
  });
  return Array.from(map.values());
};

/**
 * 体验仓展示止盈止损：以合约聚合持仓为准（getPositionList 含 null 清空）。
 * 独立体验仓列表常滞后或仍带旧价；若仍用独立列表优先，会出现接口已 null 但 UI 显示旧价。
 * 仅当聚合侧完全没有该仓位的止盈止损字段时，才保留独立列表上的价。
 */
const syncExperienceTpslFromAggregate = <T extends object>(
  experience: T[],
  aggregate: T[],
  getId: (item: T) => string,
) => {
  const aggregateById = new Map<string, T>();
  aggregate.forEach(item => {
    if (!isExperienceRecord(item as any)) return;
    aggregateById.set(getId(item), item);
  });

  return experience.map(item => {
    const agg = aggregateById.get(getId(item)) as any;
    if (!agg) return item;

    const aggProvided =
      'triggerProfitPrice' in agg ||
      'triggerStopPrice' in agg ||
      'takeProfitPrice' in agg ||
      'stopLossPrice' in agg ||
      Array.isArray(agg.entrusts) ||
      Array.isArray(agg.profitList);

    if (!aggProvided) return item;

    const prices = resolvePositionTpslPrices(agg);
    return {
      ...(item as object),
      triggerProfitPrice: prices.triggerProfitPrice,
      triggerStopPrice: prices.triggerStopPrice,
      takeProfitPrice: agg.takeProfitPrice ?? null,
      stopLossPrice: agg.stopLossPrice ?? null,
      takeProfitEntrustId: agg.takeProfitEntrustId ?? null,
      stopLossEntrustId: agg.stopLossEntrustId ?? null,
      entrusts: Array.isArray(agg.entrusts) ? agg.entrusts : [],
      profitList: Array.isArray(agg.profitList) ? agg.profitList : [],
    } as T;
  });
};

export const filterByMarginSource = <T extends object>(
  filter: MarginSourceFilter,
  normal: T[],
  experience: T[],
  getId: (item: T) => string,
): T[] => {
  const taggedNormal = tagNormalSource(normal).filter(item => !isExperienceRecord(item as any));
  const experienceBase = experience.length
    ? experience
    : tagNormalSource(normal).filter(item => isExperienceRecord(item as any));
  // 用完整 normal（含体验仓行）同步止盈止损，避免独立列表旧价盖住聚合 null
  const taggedExperience = syncExperienceTpslFromAggregate(experienceBase, normal, getId);
  if (filter === 'NORMAL') return taggedNormal;
  if (filter === 'POSITION_EXPERIENCE') return taggedExperience;
  return mergeBySourceId(taggedNormal, taggedExperience, getId);
};

export const fetchExperiencePositionCurrent = async (symbolId?: number) => {
  const data = await welfareGet<
    ExperiencePositionDisplay[] | ExperienceCursorPage<ExperiencePositionDisplay>
  >(API.WELFARE_EP_POSITION_CURRENT, { params: cleanParams({ symbolId }) });
  return asList(data).map(mapExperiencePositionToPositionItem);
};

export const fetchExperienceEntrustCurrent = async (symbolId?: number) => {
  const data = await welfareGet<ExperienceEntrustDisplay[] | ExperienceCursorPage<ExperienceEntrustDisplay>>(
    API.WELFARE_EP_ENTRUST_CURRENT,
    { params: cleanParams({ symbolId }) },
  );
  return asList(data).map(mapExperienceEntrustToProfit);
};

export const fetchExperienceEntrustHistory = async (params?: {
  symbolId?: number;
  startTime?: number;
  endTime?: number;
  beforeTime?: number;
  beforeId?: number;
  size?: number;
  /** 聚合模式游标；独立接口勿混用 */
  cursor?: string;
}) => {
  const data = await welfareGet<ExperienceCursorPage<ExperienceEntrustDisplay>>(API.WELFARE_EP_ENTRUST_HISTORY, {
    params: cleanParams(params),
  });
  const page = data && !Array.isArray(data) ? data : { items: asList(data), hasNext: false };
  return {
    list: asList(page).map(mapExperienceEntrustToProfit),
    pageInfo: {
      hasPrev: false,
      hasNext: Boolean(page.hasNext),
    } as ScrollPageInfo,
    nextTime: page.nextTime,
    nextId: page.nextId,
    nextCursor: page.nextCursor,
  };
};

export const fetchExperienceOrderHistory = async (params?: {
  symbolId?: number;
  startTime?: number;
  endTime?: number;
  beforeTime?: number;
  beforeId?: number;
  size?: number;
  cursor?: string;
}) => {
  const data = await welfareGet<ExperienceCursorPage<ExperienceOrderDisplay>>(API.WELFARE_EP_ORDER_HISTORY, {
    params: cleanParams(params),
  });
  const page = data && !Array.isArray(data) ? data : { items: asList(data), hasNext: false };
  return {
    list: asList(page).map(mapExperienceOrderToHistory),
    pageInfo: {
      hasPrev: false,
      hasNext: Boolean(page.hasNext),
    } as ScrollPageInfo,
    nextTime: page.nextTime,
    nextId: page.nextId,
    nextCursor: page.nextCursor,
  };
};

/** 体验仓市价全部平仓 */
export const closeExperiencePosition = async (params: {
  requestId: string;
  positionId: number | string;
}) =>
  welfarePost(API.WELFARE_EP_CLOSE, {
    requestId: params.requestId,
    positionId: String(params.positionId),
  });

const epRequestId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`.slice(0, 48);

/** 体验仓业务失败仍是 HTTP 200 + code=0，只能靠 data.resultCode 判定 */
export class ExperienceResultError extends Error {
  resultCode: string;

  constructor(resultCode: string) {
    super(resultCode);
    this.name = 'ExperienceResultError';
    this.resultCode = resultCode;
  }
}

const ensureExperienceSuccess = <T extends { resultCode?: string } | undefined>(data: T) => {
  const code = data?.resultCode;
  if (code && code !== 'SUCCESS') throw new ExperienceResultError(code);
  return data;
};

/** resultCode 由前端国际化；文案缺失时回落到调用方给的兜底 */
export const experienceErrorMessage = (
  error: any,
  t: (key: string, options?: Record<string, unknown>) => string,
  fallbackKey = 'welfareCenter.experience.error.FAILED',
) => {
  const fallback = t(fallbackKey);
  const code = error instanceof ExperienceResultError ? error.resultCode : error?.resultCode;
  if (code) {
    return t(`welfareCenter.experience.error.${code}`, { defaultValue: fallback });
  }
  return error?.error?.msg || fallback;
};

/** Step24：新增体验仓止盈或止损（一次一种类型，数量由后端固定全仓） */
export const createExperienceEntrust = async (params: {
  positionId: number | string;
  entrustType: 'TAKE_PROFIT' | 'STOP_LOSS';
  triggerPrice: string | number;
  requestId?: string;
}) =>
  ensureExperienceSuccess(
    await welfarePost(API.WELFARE_EP_ENTRUST_CREATE, {
      requestId: params.requestId || epRequestId(params.entrustType === 'TAKE_PROFIT' ? 'ep-tp-c' : 'ep-sl-c'),
      positionId: String(params.positionId),
      entrustType: params.entrustType,
      triggerPrice: Number(params.triggerPrice),
    }),
  );

/**
 * Step24：新增体验仓移动止盈止损。
 * 全仓 + 标记价格由后端固定；triggerPrice 由后端按有利极值动态计算，前端不得传。
 * activatePrice 可省略（按创建时标记价立即激活）；distanceRate 与 distancePrice 必须且只能传一个。
 */
export const createExperienceMoveProfit = async (params: {
  positionId: number | string;
  activatePrice?: string | number | null;
  distanceRate?: string | number | null;
  distancePrice?: string | number | null;
  requestId?: string;
}) => {
  const hasRate = params.distanceRate !== undefined && params.distanceRate !== null && params.distanceRate !== '';
  const hasPrice = params.distancePrice !== undefined && params.distancePrice !== null && params.distancePrice !== '';
  if (hasRate === hasPrice) {
    throw new ExperienceResultError('MOVE_PROFIT_CONFIG_INVALID');
  }

  const hasActivate =
    params.activatePrice !== undefined && params.activatePrice !== null && params.activatePrice !== '';

  return ensureExperienceSuccess(
    await welfarePost(API.WELFARE_EP_ENTRUST_CREATE, {
      requestId: params.requestId || epRequestId('ep-move-c'),
      positionId: String(params.positionId),
      entrustType: 'MOVE_PROFIT',
      ...(hasActivate ? { activatePrice: Number(params.activatePrice) } : {}),
      ...(hasRate ? { distanceRate: Number(params.distanceRate) } : { distancePrice: Number(params.distancePrice) }),
    }),
  );
};

/** Step24：修改体验仓止盈/止损触发价（返回新 entrustId；MOVE_PROFIT 不支持修改） */
export const updateExperienceEntrust = async (params: {
  positionId: number | string;
  entrustId: number | string;
  triggerPrice: string | number;
  requestId?: string;
}) =>
  ensureExperienceSuccess(
    await welfarePost(API.WELFARE_EP_ENTRUST_UPDATE, {
      requestId: params.requestId || epRequestId('ep-ent-u'),
      positionId: String(params.positionId),
      entrustId: Number(params.entrustId),
      triggerPrice: Number(params.triggerPrice),
    }),
  );

/** Step24：取消体验仓止盈/止损/移动止盈止损 */
export const cancelExperienceEntrust = async (params: {
  positionId: number | string;
  entrustId: number | string;
}) =>
  ensureExperienceSuccess(
    await welfarePost(API.WELFARE_EP_ENTRUST_CANCEL, {
      positionId: String(params.positionId),
      entrustId: Number(params.entrustId),
    }),
  );

/** 体验仓提交全仓止盈止损：TP/SL 分两次 create/update */
export const submitExperienceAllTpsl = async (params: {
  positionId: number | string;
  triggerProfitPrice?: string;
  triggerStopPrice?: string;
  existingProfitEntrustId?: string | number;
  existingStopEntrustId?: string | number;
}) => {
  const tasks: Promise<unknown>[] = [];
  const { positionId, triggerProfitPrice, triggerStopPrice } = params;

  if (triggerProfitPrice && new BigNumber(triggerProfitPrice).gt(0)) {
    if (params.existingProfitEntrustId) {
      tasks.push(
        updateExperienceEntrust({
          positionId,
          entrustId: params.existingProfitEntrustId,
          triggerPrice: triggerProfitPrice,
        }),
      );
    } else {
      tasks.push(
        createExperienceEntrust({
          positionId,
          entrustType: 'TAKE_PROFIT',
          triggerPrice: triggerProfitPrice,
        }),
      );
    }
  }

  if (triggerStopPrice && new BigNumber(triggerStopPrice).gt(0)) {
    if (params.existingStopEntrustId) {
      tasks.push(
        updateExperienceEntrust({
          positionId,
          entrustId: params.existingStopEntrustId,
          triggerPrice: triggerStopPrice,
        }),
      );
    } else {
      tasks.push(
        createExperienceEntrust({
          positionId,
          entrustType: 'STOP_LOSS',
          triggerPrice: triggerStopPrice,
        }),
      );
    }
  }

  if (!tasks.length) return [];
  return Promise.all(tasks);
};
